import * as React from 'react';
import { observer } from 'mobx-react';

import { WebGivingPaymentResultStatus, ResetPageReason, PaymentLabel, WebGivingAddPaymentMethodResultCode } from '../../webgiving-generated';
import { Alert } from '../alert/alert';
import { Link, LinkProps } from '../../primitives/link/link';
import { WebGivingUserAction } from '../../webgiving-user-actions';
import { injectWebGivingProps, InjectableWebGivingProps, raiseAction } from '../../utils/injectable-props';
import { scrollErrorIntoViewOnNextFrame } from '../../utils/error-focuser';
import { IParams, Text } from '../text';

import { description, paymentError, nowrap } from './error-message.less';

export enum WebGivingErrorType {
	Generic,
	PaymentError,
	MerchantConfigurationChanged,
	ShouldReloadPage,
	AddPaymentMethodError,
}

export interface IGenericError {
	errorType: WebGivingErrorType.Generic;
	message: string;
}

export interface IAddPaymentMethodError {
	errorType: WebGivingErrorType.AddPaymentMethodError;
	errorResultCode: WebGivingAddPaymentMethodResultCode;
	errorMessageLines: string[];
}

export interface IPaymentErrorDetails {
	errorType: WebGivingErrorType.PaymentError;
	errorReason: WebGivingPaymentResultStatus;
	messageParams?: IParams;
	message: string;
	failedPaymentMethodId: string | undefined;
	paymentLabel: PaymentLabel;
	singlePaymentMethod: boolean;
}

export interface IMerchantConfigurationChangedError {
	errorType: WebGivingErrorType.MerchantConfigurationChanged;
}

export interface IShouldReloadPageError {
	errorType: WebGivingErrorType.ShouldReloadPage;
	reason: ResetPageReason;
	paymentLabel: PaymentLabel;
	resetPasswordUrl: string;
}

export const merchantConfigurationChangedError: IMerchantConfigurationChangedError = {
	errorType: WebGivingErrorType.MerchantConfigurationChanged,
};

export type WebGivingError = IGenericError | IPaymentErrorDetails | IShouldReloadPageError | IMerchantConfigurationChangedError | IAddPaymentMethodError | null | undefined;

export interface IWebGivingErrorMessageProps {
	error: WebGivingError;
	id?: string;
}

@injectWebGivingProps
@observer
export class ErrorMessage extends React.Component<IWebGivingErrorMessageProps & InjectableWebGivingProps> {

	UNSAFE_componentWillReceiveProps(nextProps: IWebGivingErrorMessageProps) {
		if (nextProps.error && nextProps.error !== this.props.error) {
			scrollErrorIntoViewOnNextFrame();
		}
	}

	render() {
		const { error, id } = this.props;

		if (!error) {
			return <Alert content={null} id={id} />;
		}

		let content: React.ReactChild;

		switch (error.errorType) {
			case WebGivingErrorType.Generic:
				content = <Text>{error.message}</Text>;
				break;
			case WebGivingErrorType.MerchantConfigurationChanged:
				content = this.conflictContent();
				break;

			case WebGivingErrorType.PaymentError:
				content = this.displayMarkupForPaymentError(error);
				break;
			case WebGivingErrorType.ShouldReloadPage:
				content = this.displayMarkupForReason(error);
				break;
			case WebGivingErrorType.AddPaymentMethodError:
				content = this.displayMarkupForAddPaymentMethodError(error);
				return <Alert content={content} id={id} multipleLines={error.errorMessageLines && error.errorMessageLines.length > 1} />;
			default:
				const unsupported: never = error;
				throw new Error(`Unsupported WebGivingErrorType ${unsupported}`);
		}

		return <Alert content={content} id={id} />;
	}

	private displayMarkupForAddPaymentMethodError(error: IAddPaymentMethodError) {
		const { errorMessageLines } = error;

		if (errorMessageLines.length === 1) {
			return errorMessageLines[0];
		}

		return (
			<div>
				{errorMessageLines.map(line => <p><Text>{line}</Text></p>)}
			</div>
		);
	}

	private displayMarkupForReason(error: IShouldReloadPageError): React.ReactChild {
		const { reason, resetPasswordUrl, paymentLabel: { NounLowerCase } } = error;

		const acceptanceTestTarget = 'error-try-again';
		switch (reason) {
			case ResetPageReason.PersonNotFound: {
				return (
					<div>
					{/* tslint:disable:max-line-length */}
					<p className={description}><Text>Your session has expired.</Text> <ErrorMessageLink acceptanceTestTargetId={acceptanceTestTarget} onClick={this.handleTryAgain}><Text>Please re-enter your details.</Text></ErrorMessageLink></p>
					{/* tslint:enable:max-line-length */}
					</div>
				);
			}
			case ResetPageReason.PaymentNotFound: {
				return (
					<div>
						{/* tslint:disable:max-line-length */}
						<p className={description}><Text>It's been too long since you started this {NounLowerCase}.</Text> <ErrorMessageLink acceptanceTestTargetId={acceptanceTestTarget} onClick={this.handleTryAgain} ><Text>Please try again.</Text></ErrorMessageLink></p>
						{/* tslint:enable:max-line-length */}
					</div>
				);
			}
			case ResetPageReason.DuplicateMobileNumber: {
				return (
					<div>
						{/* tslint:disable:max-line-length */}
						<p className={description}><Text>This mobile number you entered has already been registered.</Text> <ErrorMessageLink acceptanceTestTargetId={acceptanceTestTarget} onClick={this.handleTryAgain} ><Text>Please re-enter your {NounLowerCase}.</Text></ErrorMessageLink></p>
						{/* tslint:enable:max-line-length */}
					</div>
				);
			}
			case ResetPageReason.AdminAccountLocked: {
				return (
					<div>
						<p className={description}>
							{/* tslint:disable:max-line-length */}
							<Text>Your account is locked due to too many failed sign in attempts. Please unlock it by</Text> <ErrorMessageLink acceptanceTestTargetId={acceptanceTestTarget} href={resetPasswordUrl}><Text>setting a new password.</Text></ErrorMessageLink>
							{/* tslint:enable:max-line-length */}
						</p>
					</div>
				);
			}
			case ResetPageReason.CookiesDisabled: {
				return (
					<div>
					{/* tslint:disable:max-line-length */}
					<p className={description}><Text>Your browser cookies are disabled. Please enable cookies on your browser and</Text> <ErrorMessageLink acceptanceTestTargetId={acceptanceTestTarget} onClick={this.handleTryAgain}><Text>try again.</Text></ErrorMessageLink></p>
					{/* tslint:enable:max-line-length */}
					</div>
				);
			}
			case ResetPageReason.Unknown:
			case ResetPageReason.InvalidSignedConfig:
			default: {
				return (
					<div>
						{/* tslint:disable:max-line-length */}
						<p className={description}><Text>Our system encountered an unexpected error. We're currently looking into it.</Text> <ErrorMessageLink acceptanceTestTargetId={acceptanceTestTarget} onClick={this.handleTryAgain}><Text>Please re-enter your details.</Text></ErrorMessageLink></p>
						{/* tslint:enable:max-line-length */}
					</div>
				);
			}
		}
	}

	private displayMarkupForPaymentError(error: IPaymentErrorDetails) {
		const { errorReason, message, paymentLabel, singlePaymentMethod, messageParams } = error;

		switch (errorReason) {
			case WebGivingPaymentResultStatus.CardFail:
				const callToAction = singlePaymentMethod
					? <ErrorMessageLink onClick={this.handleNewMethod} acceptanceTestTargetId="payment-failure-new-method" ><Text>Please add a new payment method.</Text></ErrorMessageLink>
					: <Text>Please select a different payment method.</Text>;

				return (<div className={paymentError}>
					<p className={description}>
						{/* tslint:disable:max-line-length */}
						<Text>Your {paymentLabel.NounLowerCase} has failed due to an error with your payment method.</Text>{' '}{callToAction}
						{/* tsling:enable:max-line-length */}
					</p>
				</div>);
			case WebGivingPaymentResultStatus.PaymentMethodNotSupported:
				return (<div className={paymentError}>
					<p className={description}><Text>{message}</Text></p>
				</div>);
			case WebGivingPaymentResultStatus.SuspectedAccidentalDuplicate:
				return (<div className={paymentError}>
					<p className={description}><Text>{message}</Text></p>
				</div>);
			case WebGivingPaymentResultStatus.InvalidAmount:
				const flaggedMessage = <Text params={messageParams}>{message}</Text>
				return (<div className={paymentError}>
					<p className={description}>
					  {flaggedMessage} <ErrorMessageLink acceptanceTestTargetId="payment-failure-invalid-amount" onClick={this.handleTryAgain}><Text>Please re-enter your details.</Text></ErrorMessageLink>
					</p>
				</div>);
			case WebGivingPaymentResultStatus.InvalidReferences:
				return this.conflictContent();
			case WebGivingPaymentResultStatus.MaximumYearExceeded:
				return (<div className={paymentError}>
					<p className={description}>
						{/* tslint:disable:max-line-length */}
						<Text>Your recurring {paymentLabel.NounLowerCase} would end too far in the future.</Text> <ErrorMessageLink acceptanceTestTargetId="payment-failure-maximum-year-exceeded" onClick={this.handleTryAgain}><Text>Please check the entered details.</Text></ErrorMessageLink>
						{/* tslint:enable:max-line-length */}
					</p>
				</div>
				);
			case WebGivingPaymentResultStatus.Fail:
			default:
				const defaultErrorMessage = !message
					? <Text>Something went wrong with your payment, please check your details are correct and try again.</Text>
					: <Text>{message}</Text>;
				return (<div className={paymentError}>
					<p className={description}>{defaultErrorMessage}</p>
				</div>);
		}
	}

	private conflictContent = () => (
		<div>
			<Text>Your administrators were updating their settings.</Text> <ErrorMessageLink onClick={this.handleTryAgain} acceptanceTestTargetId="payment-try-again"><Text>Please try again.</Text></ErrorMessageLink>
		</div>
	)

	private handleTryAgain = () => {
		const { error } = this.props;
		let reason: ResetPageReason | undefined;
		if (error && error.errorType === WebGivingErrorType.ShouldReloadPage) {
			reason = error.reason;
		}

		raiseAction(new WebGivingUserAction.ResetPageBecauseOfError(reason), this.props);
	}

	private handleNewMethod = () => {
		raiseAction(new WebGivingUserAction.NewPaymentMethod(), this.props);
	}
}

const ErrorMessageLink: React.StatelessComponent<{
	acceptanceTestTargetId: LinkProps['acceptanceTestTargetId'];
} & React.AnchorHTMLAttributes<HTMLAnchorElement>> = (props) => {
	return (
		<Link {...props} branded={false} inheritFontSize={true} className={nowrap}>
			{props.children}
		</Link>
	);
};
